import * as Sentry from "@sentry/nuxt";
import type { Auth, Site } from "@@/types/plugins";
import type { ApiInstance } from "~/plugins/01.api";
import { apiExceptionStatus, shouldRetryWithoutAuth } from "~/utils/apiException";

export default defineNuxtRouteMiddleware(async (to, from) => {
  const nuxtApp = useNuxtApp()
  const { $site, $api, $auth } = nuxtApp as unknown as {
    $site?: Site
    $api?: ApiInstance
    $auth?: Auth
  }

  if (typeof $api?.location?.info !== 'function') {
    const requestUrl = useRequestURL()
    const bootstrapError = nuxtApp.payload.error || new Error('Nuxt API plugin was not initialized')

    Sentry.captureException(bootstrapError, {
      tags: {
        location_bootstrap: 'failed',
        bootstrap_failure: 'api_plugin_missing',
        status: 'unknown'
      },
      extra: {
        host: requestUrl.host,
        path: to.fullPath,
        missingInjection: '$api',
        bootstrapErrorFromPayload: Boolean(nuxtApp.payload.error)
      }
    })

    if (import.meta.client) {
      reloadNuxtApp({
        path: to.fullPath,
        persistState: false
      })
    }

    return abortNavigation(createError({
      statusCode: 503,
      statusMessage: 'We could not load this booking site right now. Please refresh or try again shortly.',
      fatal: true
    }))
  }

  if ($site?.location) return

  const setLocationInfo = (info: any) => {
    const locationStore = useLocationStore()
    locationStore.setLocationInfo({
      location: info.location,
      settings: info.settings,
      images: info.images,
      integrations: info.integrations,
      tabs: info.tabs,
      colours: info.colours,
      menu: info.menu,
      customPages: info.customPages,
      domain: info.domain
    })
  }

  const clearAuthState = async () => {
    if ($auth) {
      await $auth.logout()
      return
    }

    const authStore = useAuthStore()
    authStore.token = null
    authStore.user = null
  }

  const loadLocationInfo = async () => {
    try {
      return { info: await $api.location.info() as any, error: null }
    } catch (error: any) {
      if (shouldRetryWithoutAuth(error)) {
        await clearAuthState()

        try {
          return { info: await $api.location.info() as any, error: null }
        } catch (retryError: any) {
          return { info: null, error: retryError }
        }
      }

      return { info: null, error }
    }
  }

  // Best-effort recovery: attempt to (re)load location once
  const { info, error } = await loadLocationInfo()

  if (info?.location) {
    setLocationInfo(info)
    return
  }

  const status = apiExceptionStatus(error)

  if (!error || status === 404) {
    return navigateTo('https://book.app/not-found', { external: true })
  }

  const requestUrl = useRequestURL()

  Sentry.captureException(error, {
    tags: {
      location_bootstrap: 'failed',
      status: String(status || 'unknown')
    },
    extra: {
      host: requestUrl.host,
      path: to.fullPath
    }
  })

  return abortNavigation(createError({
    statusCode: status && status >= 500 ? status : 503,
    statusMessage: 'We could not load this booking site right now. Please refresh or try again shortly.',
    fatal: true
  }))
})
